import Btn from "@/components/Btn";
import { EmailInputWithValidation } from "@/components/EmailInputWithValidation";
import { useUserProjects } from "@/hooks/useProjects";
import { ILoginCredentials } from "@/interfaces/auth.interface";
import { IProject } from "@/interfaces/project.interface";
import authService from "@/services/auth.service";
import { useAppDispatch, useAppSelector } from "@/store/hooks/hooks";
import { setProjects, setSelectedProject } from "@/store/slices/projectSlice";
import { setUser } from "@/store/slices/userSlice";
import { RootState } from "@/store/store";
import { EMAIL_VALIDATOR } from "@/utils/common";
import ToastService from "@/utils/toast";
import { useMsal } from "@azure/msal-react";
import {
  Field,
  FieldWrapper,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { Input } from "@progress/kendo-react-inputs";
import { set } from "lodash";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const LoginForm = () => {
  const user = localStorage.getItem("user");

  const { instance } = useMsal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  var { data: projectsData, refetch } = useUserProjects(
    {},
    {
      enabled: !!user,
      showLoader: false,
    }
  );

  const settingsState: any = useAppSelector(
    (state: RootState) => state.settings
  );
  const [showCustomLogin, setShowCustomLogin] = useState(false);
  const [isEntraIdEnabled, setIsEntraIdEnabled] = useState(false);
  const [loginRequest, setLoginRequest] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [loginCredentials, setLoginCredentials] = useState<ILoginCredentials>({
    email: "",
    password: "",
  });

  useEffect(() => {
    if (!settingsState?.settings) return;
    const authData = settingsState.settings.auth;
    const customData = authData.find((item: any) => item.type === "Custom");

    if (customData?.enabled) {
      setShowCustomLogin(true);

      var loginDetails = localStorage.getItem("loginUser");

      if (loginDetails) {
        try {
          const parsedLoginDetails = JSON.parse(loginDetails);
          // Make sure we have the right structure
          setLoginCredentials({
            email: parsedLoginDetails.email || "",
            password: parsedLoginDetails.password || "",
          });
          console.log("Loaded credentials:", parsedLoginDetails);
        } catch (e) {
          console.error("Error parsing stored login details", e);
        }
      }
    }

    const entraIdData = authData.find((item: any) => item.type === "EntraID");

    if (entraIdData && entraIdData?.enabled) {
      const frontendSettings = entraIdData.frontend;
      const loginRequest = {
        scopes: [frontendSettings.backendScope],
      };
      setLoginRequest(loginRequest);
      setIsEntraIdEnabled(true);
    }
  }, [settingsState]);

  const handleLoginRedirect = () => {
    if (!loginRequest) return;
    instance.loginRedirect(loginRequest).catch((error) => console.log(error));
  };

  const handleSubmit = (dataItem: { [name: string]: ILoginCredentials }) => {
    localStorage.setItem("loginUser", JSON.stringify(dataItem));
    setIsSubmitted(true);

    if (!isSubmitted) {
      authService
        .login(dataItem)
        .then((response: any) => {
          if (response.data) {
            dispatch(setUser(response.data));
            refetch();
            navigate("/home", { replace: true });
          } else {
            ToastService.error("Invalid email or password. Please try again.");
          }
          setIsSubmitted(false);
        })
        .catch((error: any) => {});
    }
  };

  useEffect(() => {
    if (projectsData?.data?.length) {
      dispatch(setProjects(projectsData.data as unknown as IProject[]));
      dispatch(setSelectedProject(projectsData.data[0]));
    }
  }, [projectsData?.data]);

  useEffect(() => {
    try {
      if (!user) return;
      const userData = JSON.parse(user!);
      if (!userData) return;
      navigate("/home", { replace: true });
    } catch (error) {
      console.log("ERR: ", error);
    }
  }, []);

  const customSubmit = (formRenderProps: FormRenderProps) => {
    const formValues = {
      email: formRenderProps.valueGetter("email"),
      password: formRenderProps.valueGetter("password"),
    };

    if (formValues.email && formValues.password) {
      handleSubmit(formValues);
    }
  };
  return (
    <div className="w-full">
      <h3 className="text-[27px] cabin-500 text-[#2B3674] font-bold">
        Sign In
      </h3>
      <p className="text-[#A3AED0] text-[12px] mt-2">
        Enter your email and password to sign in!
      </p>
      <div className="w-full my-8">
        {isEntraIdEnabled && (
          <Btn
            onClick={handleLoginRedirect}
            variant="secondary"
            text={"Sign in with Company Account"}
            disabled={false}
            imgPath="./images/Alec-btn.png"
          />
        )}
      </div>
      {showCustomLogin && (
        <>
          <div className="flex gap-x-2 w-full text-[#E0E5F2] mb-6">
            <hr className="w-full mt-[10px]" />
            <p className="text-[13px] text-[#A3AED0]">or</p>
            <hr className="w-full mt-[10px]" />
          </div>
          <Form
            key={JSON.stringify(loginCredentials)} // Add this to force re-evaluation
            initialValues={loginCredentials}
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement className="mb-4">
                <fieldset className={"k-form-fieldset"}>
                  <FieldWrapper>
                    <div className="k-form-field-wrap">
                      <div className="relative loginField">
                        <Field
                          name={"email"}
                          type={"email"}
                          component={EmailInputWithValidation}
                          label={"Email*"}
                          placeholder={"mail@alec.ae"}
                          labelClassName="font-cabin font-medium mb-2 text-[var(--kendo-neutral-160)]"
                          validator={EMAIL_VALIDATOR}
                          // Add these attributes
                          autoComplete="username email"
                          id="login-email"
                        />
                      </div>
                    </div>
                  </FieldWrapper>

                  <FieldWrapper className="mb-4">
                    <div className="k-form-field-wrap">
                      <div className="relative loginField">
                        <Field
                          name={"password"}
                          type={showPassword ? "text" : "password"}
                          component={Input}
                          label={"Password*"}
                          placeholder={"Enter Password"}
                          labelClassName="font-cabin font-medium  mb-2 text-[var(--kendo-neutral-160)]"
                          // Add these attributes
                          autoComplete="current-password"
                          id="login-password"
                        />
                        {showPassword && (
                          <Eye
                            className="absolute top-[44px] right-[19px] w-4 h-4 cursor-pointer text-[#A3AED0]"
                            onClick={() => setShowPassword(false)}
                          />
                        )}
                        {!showPassword && (
                          <EyeOff
                            className="absolute top-[44px] right-[19px] w-4 h-4 cursor-pointer text-[#A3AED0]"
                            onClick={() => setShowPassword(true)}
                          />
                        )}
                      </div>
                    </div>
                  </FieldWrapper>
                </fieldset>

                <div className="flex flex-wrap items-center justify-between">
                  {/* <Checkbox label={"Remember me"} /> */}
                  <Link
                    to={`/forgot-password`}
                    className="text-[#0078D4] font-medium"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <div className="k-form-buttons">
                  <Btn
                    variant="primary"
                    text={"Sign In"}
                    disabled={
                      !formRenderProps.valueGetter("email") ||
                      !formRenderProps.valueGetter("password")
                    }
                    onClick={() => customSubmit(formRenderProps)}
                  />
                </div>
              </FormElement>
            )}
          />
        </>
      )}
    </div>
  );
};

export default LoginForm;
